import React, { useEffect, useState } from "react";
import { Sparkles, CalendarDays, Trophy, BarChart3, Archive, Bookmark } from "lucide-react";
import { useAccess, DEFAULT_PLAN } from "@/context/AccessContext";
import { toast } from "sonner";

const TABS = [
  { id: "oracle", label: "Oracle", icon: Sparkles },
  { id: "schedule", label: "Draws", icon: CalendarDays },
  { id: "scoreboard", label: "Board", icon: Trophy },
  { id: "analytics", label: "Stats", icon: BarChart3 },
  { id: "history", label: "History", icon: Archive },
  { id: "picks", label: "Picks", icon: Bookmark },
];

function useActiveSection(ids) {
  const [current, setCurrent] = useState(ids[0]);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const y = window.scrollY + window.innerHeight * 0.35;
      let best = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) best = id;
      }
      setCurrent(best);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [ids]);
  return current;
}

/* Phone-only bottom bar: section tabs for subscribers, a sticky trial CTA for everyone else. */
export function MobileNav({ onScrollTo }) {
  const { active, loading, checkingOut, subscribe, trial_days, price_usd } = useAccess();
  const ids = React.useMemo(() => TABS.map((t) => t.id), []);
  const current = useActiveSection(ids);
  if (loading) return null;

  if (!active) {
    return (
      <div data-testid="mobile-cta-bar" className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-ink/85 backdrop-blur-xl border-t border-oracle/30 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <button
          data-testid="mobile-trial-btn"
          disabled={checkingOut}
          onClick={() => subscribe(DEFAULT_PLAN).catch((e) => toast.error(e.response?.data?.detail || "Could not start checkout"))}
          className="w-full bg-oracle text-ink label-caps py-3.5 rounded-md shadow-[0_0_24px_rgba(255,176,0,0.35)] active:scale-[0.98] transition-transform disabled:opacity-60"
        >
          {checkingOut ? "Opening checkout…" : `Start ${trial_days}-day free trial · then $${price_usd.toFixed(2)}/mo`}
        </button>
      </div>
    );
  }

  return (
    <nav data-testid="mobile-nav" aria-label="Sections" className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-ink/85 backdrop-blur-xl border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
      <ul className="grid grid-cols-6">
        {TABS.map(({ id, label, icon: Icon }) => {
          const on = current === id;
          return (
            <li key={id}>
              <button
                data-testid={`mobile-nav-${id}`}
                aria-current={on ? "true" : undefined}
                onClick={() => onScrollTo?.(id)}
                className={`w-full flex flex-col items-center gap-1 pt-2.5 pb-2 transition-colors ${on ? "text-oracle" : "text-parchment/50 active:text-parchment"}`}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={on ? 2 : 1.5} />
                <span className="font-body text-[9px] uppercase tracking-[0.14em]">{label}</span>
                <span className={`h-0.5 w-6 rounded-full transition-colors ${on ? "bg-oracle" : "bg-transparent"}`} />
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
